import React, { useMemo, Fragment, ReactElement } from 'react';
import Card from '../../../../components/card/card.component';
import Table from '../../../../components/table/table.component';
import TableHead from '../../../../components/table-head/table-head.component';
import TableBody from '../../../../components/table-body/table-body.component';
import TableRow from '../../../../components/table-row/table-row.component';
import TableCell from '../../../../components/table-cell/table-cell.component';
import PaddedFlexContainer from '../../../../components/padded-flex-container/padded-flex-container.component';
import FlexContainer from '../../../../components/flex-container/flex-container.component';
import ExpansionGroup from '../../../../components/expansion-group/expansion-group.component';
import Widget from '../../../../components/widget/widget.component';
import WidgetRow from '../../../../components/widget-row/widget-row.component';
import HintLinked from '../../../../components/hint-linked/hint-linked.component';
import Tooltip from '../../../../components/tooltip/tooltip.component';
import TextBlock from '../../../../components/text-block/text-block.component';
import ActionButton from '../../../../components/action-button/action-button.component';
import RequiredMark from '../../../../components/required-mark/required-mark.component';
import FormFieldSelect from '../../../../components/form-field-select/form-field-select.component';
import FormFieldText from '../../../../components/form-field-text/form-field-text.component';
import ActionButtonContainer from '../../../../components/action-button-container/action-button-container.component';
import ActionButtonNewItem from '../../../../components/action-button-new-item/action-button-new-item.component';
import TableRowAddNewItem from '../../../../components/table-row-add-new-item/table-row-add-new-item.component';
import VisualEditorSectionBlock
  from '../../../../components/visual-editor-section-block/visual-editor-section-block.component';
import MenuItemList from '../../../../components/menu-item-list/menu-item-list.component';
import SinglePageTab from '../../../app/common/single-page-tab/single-page-tab.component';
import RemoveIcon from '../../../../icons/remove-icon';
import FormService from '../../../../services/form-service';
import StructureService from '../../../../services/structure-service';
import useScreenSmall from '../../../../hooks/use-screen-small';
import { DEAL_TEMPLATE_TYPE_NEW_DEAL_TEMPLATE } from '../../../../constants/entity/deal-template-types';
import {
  DEAL_TEMPLATE_KEY_AMOUNT, DEAL_TEMPLATE_KEY_APPRAISED_VALUE_AMOUNT, DEAL_TEMPLATE_KEY_ASKING_PRICE,
  DEAL_TEMPLATE_KEY_BROKER, DEAL_TEMPLATE_KEY_DEAL_CAPITALIZATION, DEAL_TEMPLATE_KEY_DEAL_CONTACT,
  DEAL_TEMPLATE_KEY_DEAL_EXTERNAL_LINKS, DEAL_TEMPLATE_KEY_DEAL_FILES, DEAL_TEMPLATE_KEY_DEAL_INVESTMENT_RETURNS,
  DEAL_TEMPLATE_KEY_DEAL_MILESTONES, DEAL_TEMPLATE_KEY_DEAL_OVERVIEW, DEAL_TEMPLATE_KEY_DEAL_PHOTOS,
  DEAL_TEMPLATE_KEY_DEAL_STRUCTURE, DEAL_TEMPLATE_KEY_DEAL_TERM, DEAL_TEMPLATE_KEY_EQUITY_AMOUNT,
  DEAL_TEMPLATE_KEY_EQUITY_REQUIRED, DEAL_TEMPLATE_KEY_ESTIMATED_TDC, DEAL_TEMPLATE_KEY_EXTENSION_CONDITIONS,
  DEAL_TEMPLATE_KEY_LENDER, DEAL_TEMPLATE_KEY_LOAN_AMOUNT, DEAL_TEMPLATE_KEY_LOAN_TYPE,
  DEAL_TEMPLATE_KEY_PROJECTED_NOI_AMOUNT, DEAL_TEMPLATE_KEY_RFP, DEAL_TEMPLATE_KEY_SPONSOR,
  DEAL_TEMPLATE_KEY_TOTAL_FUNDED, DEAL_TEMPLATE_KEY_TOTAL_UNFUNDED, DEAL_TEMPLATE_KEY_LOT_SIZE,
  DEAL_TEMPLATE_KEY_DEAL_CONTACTS, DEAL_TEMPLATE_KEY_DEAL_BUILDING_DETAILS_ASSET_USE_BREAKDOWN,
  DEAL_TEMPLATE_KEY_DEAL_ALLOCATION,
} from '../../../../constants/entity/deal-template-keys';

const DealTemplateSummary: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const COLUMNS_TOTAL_COMMON: number = 2;
  const COLUMNS_TOTAL_CUSTOM_SECTION: number = 3;
  const DEAL_ASSET_USE_BREAKDOWN_HEADERS_LIMIT: number = 5;
  const WIDGET_NAME_PREFIX_DEAL_TEMPLATE_SECTION: string = 'widget-deal-template-section';

  const {
    isSaving,
    isSaveDisabled,
    isEditMode,
    isRightDrawerOpen,
    isActive,
    formError,
    dealTemplateData,
    dealTemplateInitialData,
    dealTemplateCustomSections,
    portfolioList,
    measureUnitList,
    fieldTypeList,
    fieldListTypeList,
    dealTemplateTypeList,
    onCancelForm,
    onSubmitForm,
    onEditModeButtonClick,
    onDealAssetUseBreakdownHeaderAdd,
    onDealAssetUseBreakdownHeaderDelete,
    onDealInvestmentReturnsItemAdd,
    onDealInvestmentReturnsItemDelete,
    onDealContactsGroupAdd,
    onDealContactsGroupDelete,
    onDealCapitalizationSourceGroupAdd,
    onDealCapitalizationSourceGroupDelete,
    onDealFileGroupAdd,
    onDealFileGroupDelete,
    onDealTemplateFieldVisibilityChange,
    onDealTemplateCustomSectionAdd,
    onDealTemplateCustomSectionFieldAdd,
    onDealTemplateCustomSectionDelete,
    onDealTemplateCustomSectionFieldDelete,
    onDealTemplateCustomSectionFieldTypeChange,
    onCheckIfDealTemplateHiddenFieldVisible,
    onCheckIfDealTemplateSectionFieldDropdown,
    onCheckIfDealTemplateSectionFieldUsed,
  } = props;

  const isSmallScreen: boolean = useScreenSmall();

  const isMultiplePortfolioGroupView: boolean = useMemo((): boolean => (
    portfolioList && portfolioList.length ? portfolioList
      .map((portfolioItem: Portfolio): number => portfolioItem.group && portfolioItem.group.id)
      .filter((portfolioGroupId: number, portfolioGroupIndex: number, portfolioIdList: number[]): boolean => (
        portfolioIdList.indexOf(portfolioGroupId) === portfolioGroupIndex
      )).length > 1 : false
  ), [portfolioList]);

  const dealTemplateType: string | undefined = dealTemplateData ? dealTemplateData.type : undefined;
  const dealTemplateInitialDataPortfolioId: number | null = (dealTemplateInitialData && dealTemplateInitialData.asset
    && dealTemplateInitialData.asset.portfolioId) || null;
  const dealTemplateInitialDataPortfolio: Portfolio | null = portfolioList && portfolioList.length
    ? portfolioList.find((portfolioItem: Portfolio): boolean => (
      portfolioItem.id === dealTemplateInitialDataPortfolioId
    )) : null;
  const dealTemplateInitialDataPortfolioName: string | null = dealTemplateInitialDataPortfolio?.name || null;
  const dealTemplateInitialDataDealAssetUseBreakdownHeaders: DealAssetUseBreakdownHeader[] = (dealTemplateInitialData
    && dealTemplateInitialData.dealBuildingDetailsAssetUseBreakdownHeaders) || [];
  const dealTemplateInitialDataDealInvestmentReturnsItem: DealInvestmentReturnsItem[] = (dealTemplateInitialData
    && dealTemplateInitialData.dealInvestmentReturns) || [];
  const dealTemplateInitialDataDealContacts: DealContactGroup[] = (dealTemplateInitialData
    && dealTemplateInitialData.dealContacts) || [];
  const dealTemplateInitialDataDealCapitalizationSourcesGroups: DealCapitalizationGroup[] = (dealTemplateInitialData
    && dealTemplateInitialData.dealCapitalization && dealTemplateInitialData.dealCapitalization.sources) || [];
  const dealTemplateInitialDataDealFileGroups: DealFileGroup[] = (dealTemplateInitialData
    && dealTemplateInitialData.dealFileGroups) || [];
  const initialDataFormPrefix: string = 'initialData';
  const hiddenFieldsFormPrefix: string = 'hiddenFields';
  const customSectionsFormPrefix: string = 'customSections';
  const isNewDealAssetUseBreakdownHeaderAvailable: boolean = (
    dealTemplateInitialDataDealAssetUseBreakdownHeaders.length < DEAL_ASSET_USE_BREAKDOWN_HEADERS_LIMIT
  );

  const menuItemDealTemplateTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: dealTemplateTypeList,
  }), [dealTemplateTypeList]);

  const menuItemPortfolioList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    isGrouped: isMultiplePortfolioGroupView,
    groupKey: 'group',
    groupItemLabelKey: 'name',
    optionItemLabelKey: 'name',
    optionList: portfolioList,
  }), [portfolioList, isMultiplePortfolioGroupView]);

  const menuItemMeasureUnitList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: measureUnitList,
  }), [measureUnitList]);

  const menuItemFieldTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: fieldTypeList,
  }), [fieldTypeList]);

  const menuItemFieldListTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: fieldListTypeList,
  }), [fieldListTypeList]);

  const measureUnitMap: Record<string, MeasureUnit> = useMemo((): Record<string, MeasureUnit> => (
    StructureService.getItemMap(measureUnitList, 'name')
  ), [measureUnitList]);

  const fieldTypeMap: Record<string, FieldType> = useMemo((): Record<string, FieldType> => (
    StructureService.getItemMap(fieldTypeList, 'name')
  ), [fieldTypeList]);

  const fieldListTypeMap: Record<string, FieldListType> = useMemo((): Record<string, FieldListType> => (
    StructureService.getItemMap(fieldListTypeList, 'name')
  ), [fieldListTypeList]);

  const deleteSectionDropDeps: DepsItem[] = useMemo((): DepsItem[] => [
    dealTemplateCustomSections,
  ], [dealTemplateCustomSections]);

  return (
    <SinglePageTab
      isSaving={isSaving}
      isSaveDisabled={isSaveDisabled}
      isEditMode={isEditMode}
      isHidden={!isActive}
      isRightDrawerOpen={isRightDrawerOpen}
      formError={formError}
      onCancelForm={onCancelForm}
      onSubmitForm={onSubmitForm}
    >
      <Table isFullWidth>
        <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
          <TableRow>
            <TableCell>
              Deal Template Title
              {
                isEditMode && (
                  <RequiredMark />
                )
              }
            </TableCell>
            <TableCell>
              {
                isEditMode ? (
                  <FormFieldText isBordered name="title" />
                ) : dealTemplateData.title
              }
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              Deal Template Type
              {
                isEditMode && (
                  <RequiredMark />
                )
              }
            </TableCell>
            <TableCell>
              {
                isEditMode ? (
                  <FormFieldSelect
                    isBordered
                    isDisabled
                    name="type"
                    optionsTotal={dealTemplateTypeList && dealTemplateTypeList.length}
                  >
                    {menuItemDealTemplateTypeList}
                  </FormFieldSelect>
                ) : dealTemplateData.typeText
              }
            </TableCell>
          </TableRow>
          {
            (dealTemplateData.description || isEditMode) && (
              <TableRow>
                <TableCell>
                  Deal Template Description
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText
                        isBordered
                        isMultiline
                        name="description"
                        maxRows={FormService.TEXTAREA_MAX_ROWS_MEDIUM}
                        minRows={FormService.TEXTAREA_MIN_ROWS_DEFAULT}
                      />
                    ) : dealTemplateData.description
                  }
                </TableCell>
              </TableRow>
            )
          }
        </TableBody>
      </Table>
      {
        dealTemplateType === DEAL_TEMPLATE_TYPE_NEW_DEAL_TEMPLATE && (
          <PaddedFlexContainer isColumn isFullWidth>
            <ExpansionGroup isFixed isExpanded isFullWidth withOffset headerComponent="Deal Common Info">
              <Card isContainer isFullWidth>
                <Table>
                  <TableBody
                    withNoBorders
                    withTopAlign
                    withSmallPadding
                    withTwoEqualColumns={!isSmallScreen}
                    withBlockCells={isSmallScreen}
                  >
                    <TableRow>
                      <TableCell>
                        <FlexContainer isSpread>
                          <TextBlock>
                            Name
                          </TextBlock>
                          <VisualEditorSectionBlock isDisabled isVisible isInlineField isEditMode={isEditMode} />
                        </FlexContainer>
                      </TableCell>
                      <TableCell>
                        <FlexContainer isSpread>
                          <TextBlock>
                            Status
                          </TextBlock>
                          <VisualEditorSectionBlock isDisabled isVisible isInlineField isEditMode={isEditMode} />
                        </FlexContainer>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <FlexContainer isSpread>
                          <TextBlock>
                            Deal Lead
                          </TextBlock>
                          <VisualEditorSectionBlock
                            isInlineField
                            isEditMode={isEditMode}
                            isVisible={onCheckIfDealTemplateHiddenFieldVisible(DEAL_TEMPLATE_KEY_DEAL_CONTACT)}
                            fieldName={`${hiddenFieldsFormPrefix}[${DEAL_TEMPLATE_KEY_DEAL_CONTACT}].isVisible`}
                            onVisibilityChange={(isVisible: boolean): void => {
                              onDealTemplateFieldVisibilityChange(isVisible, DEAL_TEMPLATE_KEY_DEAL_CONTACT);
                            }}
                          />
                        </FlexContainer>
                      </TableCell>
                      <TableCell>
                        <FlexContainer isSpread>
                          <TextBlock>
                            Portfolio
                            {
                              !isEditMode && !!dealTemplateInitialDataPortfolioName && (
                                ` (${dealTemplateInitialDataPortfolioName})`
                              )
                            }
                          </TextBlock>
                          <VisualEditorSectionBlock isDisabled isVisible isInlineField isEditMode={isEditMode} />
                        </FlexContainer>
                        {
                          isEditMode && (
                            <FlexContainer isSpread>
                              {
                                isEditMode && (
                                  <FormFieldSelect
                                    isBordered
                                    withRightOffset
                                    withBlankElement
                                    name={`${initialDataFormPrefix}.asset.portfolioId`}
                                  >
                                    {menuItemPortfolioList}
                                  </FormFieldSelect>
                                )
                              }
                            </FlexContainer>
                          )
                        }
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <FlexContainer isSpread>
                          <TextBlock>
                            Sponsor
                          </TextBlock>
                          <VisualEditorSectionBlock
                            isInlineField
                            isEditMode={isEditMode}
                            isVisible={onCheckIfDealTemplateHiddenFieldVisible(DEAL_TEMPLATE_KEY_SPONSOR)}
                            fieldName={`${hiddenFieldsFormPrefix}[${DEAL_TEMPLATE_KEY_SPONSOR}].isVisible`}
                            onVisibilityChange={(isVisible: boolean): void => {
                              onDealTemplateFieldVisibilityChange(isVisible, DEAL_TEMPLATE_KEY_SPONSOR);
                            }}
                          />
                        </FlexContainer>
                      </TableCell>
                      <TableCell>
                        <FlexContainer isSpread>
                          <TextBlock>
                            Broker
                          </TextBlock>
                          <VisualEditorSectionBlock
                            isInlineField
                            isEditMode={isEditMode}
                            isVisible={onCheckIfDealTemplateHiddenFieldVisible(DEAL_TEMPLATE_KEY_BROKER)}
                            fieldName={`${hiddenFieldsFormPrefix}[${DEAL_TEMPLATE_KEY_BROKER}].isVisible`}
                            onVisibilityChange={(isVisible: boolean): void => {
                              onDealTemplateFieldVisibilityChange(isVisible, DEAL_TEMPLATE_KEY_BROKER);
                            }}
                          />
                        </FlexContainer>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <FlexContainer isSpread>
                          <TextBlock>
                            Lender
                          </TextBlock>
                          <VisualEditorSectionBlock
                            isInlineField
                            isEditMode={isEditMode}
                            isVisible={onCheckIfDealTemplateHiddenFieldVisible(DEAL_TEMPLATE_KEY_LENDER)}
                            fieldName={`${hiddenFieldsFormPrefix}[${DEAL_TEMPLATE_KEY_LENDER}].isVisible`}
                            onVisibilityChange={(isVisible: boolean): void => {
                              onDealTemplateFieldVisibilityChange(isVisible, DEAL_TEMPLATE_KEY_LENDER);
                            }}
                          />
                        </FlexContainer>
                      </TableCell>
                      <TableCell>
                        <FlexContainer isSpread>
                          <TextBlock>
                            Deal Structure
                          </TextBlock>
                          <VisualEditorSectionBlock
                            isInlineField
                            isEditMode={isEditMode}
                            isVisible={onCheckIfDealTemplateHiddenFieldVisible(DEAL_TEMPLATE_KEY_DEAL_STRUCTURE)}
                            fieldName={`${hiddenFieldsFormPrefix}[${DEAL_TEMPLATE_KEY_DEAL_STRUCTURE}].isVisible`}
                            onVisibilityChange={(isVisible: boolean): void => {
                              onDealTemplateFieldVisibilityChange(isVisible, DEAL_TEMPLATE_KEY_DEAL_STRUCTURE);
                            }}
                          />
                        </FlexContainer>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Card>
            </ExpansionGroup>
            <FlexContainer isSpread withNoAlign isColumn={isSmallScreen}>
              <WidgetRow isColumn={!isSmallScreen}>
                <Widget isFullWidth isFullHeight defaultWidgetTitle="Deal Property Details">
                  <Table>
                    <TableBody isPlain withZebraRows withTwoColumns withHeaderColumn>
                      <TableRow>
                        <TableCell>
                          Address
                        </TableCell>
                        <TableCell>
                          <VisualEditorSectionBlock isDisabled isVisible isInlineField isEditMode={isEditMode} />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          Property Type
                        </TableCell>
                        <TableCell>
                          <VisualEditorSectionBlock isDisabled isVisible isInlineField isEditMode={isEditMode} />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          Lot Size
                        </TableCell>
                        <TableCell>
                          <VisualEditorSectionBlock
                            isInlineField
                            isEditMode={isEditMode}
                            isVisible={onCheckIfDealTemplateHiddenFieldVisible(DEAL_TEMPLATE_KEY_LOT_SIZE)}
                            fieldName={`${hiddenFieldsFormPrefix}[${DEAL_TEMPLATE_KEY_LOT_SIZE}].isVisible`}
                            onVisibilityChange={(isVisible: boolean): void => {
                              onDealTemplateFieldVisibilityChange(isVisible, DEAL_TEMPLATE_KEY_LOT_SIZE);
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  <ExpansionGroup isFixed isExpanded isDefaultExpanded headerComponent="Asset Use Breakdown">
                    <VisualEditorSectionBlock
                      isEditMode={isEditMode}
                      isVisible={onCheckIfDealTemplateHiddenFieldVisible(
                        DEAL_TEMPLATE_KEY_DEAL_BUILDING_DETAILS_ASSET_USE_BREAKDOWN,
                      )}
                      fieldName={`${hiddenFieldsFormPrefix}`
                        + `[${DEAL_TEMPLATE_KEY_DEAL_BUILDING_DETAILS_ASSET_USE_BREAKDOWN}].isVisible`}
                      onVisibilityChange={(isVisible: boolean): void => {
                        onDealTemplateFieldVisibilityChange(
                          isVisible, DEAL_TEMPLATE_KEY_DEAL_BUILDING_DETAILS_ASSET_USE_BREAKDOWN,
                        );
                      }}
                    />
                    <Table>
                      <TableHead isSubHeader withFixedCellsSize>
                        {
                          dealTemplateInitialDataDealAssetUseBreakdownHeaders
                          && !!dealTemplateInitialDataDealAssetUseBreakdownHeaders.length &&
                          dealTemplateInitialDataDealAssetUseBreakdownHeaders.map((
                            dealAssetUseBreakdownHeader: DealAssetUseBreakdownHeader,
                          ): ReactElement => {
                            const formKeyDealAssetUseBreakdownHeader: string = FormService.getIndexedFormFieldKey(
                              dealAssetUseBreakdownHeader.id,
                            );

                            return (
                              <TableRow key={dealAssetUseBreakdownHeader.id}>
                                <TableCell>
                                  {
                                    !isEditMode && measureUnitMap && dealAssetUseBreakdownHeader.name
                                    && measureUnitMap[dealAssetUseBreakdownHeader.name]
                                    && measureUnitMap[dealAssetUseBreakdownHeader.name].title
                                  }
                                  {
                                    isEditMode && (
                                      <FormFieldSelect
                                        isLight
                                        withBlankElement
                                        isDisabled={measureUnitList && !measureUnitList.length}
                                        name={`${initialDataFormPrefix}.dealBuildingDetailsAssetUseBreakdownHeaders`
                                          + `[${formKeyDealAssetUseBreakdownHeader}].name`}
                                      >
                                        {menuItemMeasureUnitList}
                                      </FormFieldSelect>
                                    )
                                  }
                                </TableCell>
                                <TableCell isTextRight>
                                  {
                                    isEditMode && (
                                      <ActionButton
                                        isLight
                                        isInline
                                        withNoHighlight
                                        withNoPadding
                                        onClick={(): void => {
                                          onDealAssetUseBreakdownHeaderDelete(dealAssetUseBreakdownHeader.id);
                                        }}
                                      >
                                        <RemoveIcon />
                                      </ActionButton>
                                    )
                                  }
                                </TableCell>
                              </TableRow>
                            );
                          })
                        }
                        {
                          isEditMode && isNewDealAssetUseBreakdownHeaderAvailable && (
                            <TableRowAddNewItem
                              isLight
                              colSpan={COLUMNS_TOTAL_COMMON}
                              onClick={onDealAssetUseBreakdownHeaderAdd}
                            />
                          )
                        }
                      </TableHead>
                    </Table>
                  </ExpansionGroup>
                </Widget>
                <Widget isFullWidth isFullHeight defaultWidgetTitle="Deal Overview">
                  <VisualEditorSectionBlock
                    isEditMode={isEditMode}
                    isVisible={onCheckIfDealTemplateHiddenFieldVisible(DEAL_TEMPLATE_KEY_DEAL_OVERVIEW)}
                    fieldName={`${hiddenFieldsFormPrefix}[${DEAL_TEMPLATE_KEY_DEAL_OVERVIEW}].isVisible`}
                    onVisibilityChange={(isVisible: boolean): void => {
                      onDealTemplateFieldVisibilityChange(isVisible, DEAL_TEMPLATE_KEY_DEAL_OVERVIEW);
                    }}
                  />
                </Widget>
                <Widget isFullWidth isFullHeight defaultWidgetTitle="External Links">
                  <VisualEditorSectionBlock
                    isEditMode={isEditMode}
                    isVisible={onCheckIfDealTemplateHiddenFieldVisible(DEAL_TEMPLATE_KEY_DEAL_EXTERNAL_LINKS)}
                    fieldName={`${hiddenFieldsFormPrefix}[${DEAL_TEMPLATE_KEY_DEAL_EXTERNAL_LINKS}].isVisible`}
                    onVisibilityChange={(isVisible: boolean): void => {
                      onDealTemplateFieldVisibilityChange(isVisible, DEAL_TEMPLATE_KEY_DEAL_EXTERNAL_LINKS);
                    }}
                  />
                </Widget>
                <Widget isFullWidth isFullHeight defaultWidgetTitle="Deal Photos">
                  <VisualEditorSectionBlock
                    isEditMode={isEditMode}
                    isVisible={onCheckIfDealTemplateHiddenFieldVisible(DEAL_TEMPLATE_KEY_DEAL_PHOTOS)}
                    fieldName={`${hiddenFieldsFormPrefix}[${DEAL_TEMPLATE_KEY_DEAL_PHOTOS}].isVisible`}
                    onVisibilityChange={(isVisible: boolean): void => {
                      onDealTemplateFieldVisibilityChange(isVisible, DEAL_TEMPLATE_KEY_DEAL_PHOTOS);
                    }}
                  />
                </Widget>
                <Widget isFullWidth isFullHeight defaultWidgetTitle="Allocation">
                  <VisualEditorSectionBlock
                    isEditMode={isEditMode}
                    isVisible={onCheckIfDealTemplateHiddenFieldVisible(DEAL_TEMPLATE_KEY_DEAL_ALLOCATION)}
                    fieldName={`${hiddenFieldsFormPrefix}[${DEAL_TEMPLATE_KEY_DEAL_ALLOCATION}].isVisible`}
                    onVisibilityChange={(isVisible: boolean): void => {
                      onDealTemplateFieldVisibilityChange(isVisible, DEAL_TEMPLATE_KEY_DEAL_ALLOCATION);
                    }}
                  />
                </Widget>
                <Widget isFullWidth isFullHeight defaultWidgetTitle="Deal Files">
                  <VisualEditorSectionBlock
                    isEditMode={isEditMode}
                    isVisible={onCheckIfDealTemplateHiddenFieldVisible(DEAL_TEMPLATE_KEY_DEAL_FILES)}
                    fieldName={`${hiddenFieldsFormPrefix}[${DEAL_TEMPLATE_KEY_DEAL_FILES}].isVisible`}
                    onVisibilityChange={(isVisible: boolean): void => {
                      onDealTemplateFieldVisibilityChange(isVisible, DEAL_TEMPLATE_KEY_DEAL_FILES);
                    }}
                  />
                  {
                    dealTemplateInitialDataDealFileGroups && !!dealTemplateInitialDataDealFileGroups.length
                    && dealTemplateInitialDataDealFileGroups.map((dealFileGroup: DealFileGroup): ReactElement => {
                      const formKeyDealFileGroup: string = FormService.getIndexedFormFieldKey(dealFileGroup.id);

                      return (
                        <ExpansionGroup
                          isFixed
                          isExpanded
                          isDefaultExpanded
                          key={dealFileGroup.id}
                          headerComponent={(
                            <FlexContainer isSpread>
                              <FlexContainer isSpread isStart>
                                {
                                  !isEditMode && dealFileGroup.name
                                }
                                {
                                  isEditMode && (
                                    <Fragment>
                                      <FormFieldText
                                        isHidden
                                        name={`${initialDataFormPrefix}.dealFileGroups[${formKeyDealFileGroup}].id`}
                                      />
                                      <FormFieldText
                                        isLight
                                        isLimited
                                        withUnderline
                                        placeholder="Name"
                                        name={`${initialDataFormPrefix}.dealFileGroups[${formKeyDealFileGroup}].name`}
                                      />
                                    </Fragment>
                                  )
                                }
                              </FlexContainer>
                              {
                                isEditMode && (
                                  <ActionButtonContainer>
                                    <Tooltip title="Delete">
                                      <ActionButton
                                        isLight
                                        withNoPadding
                                        onClick={(): void => {
                                          onDealFileGroupDelete(dealFileGroup.id);
                                        }}
                                      >
                                        <RemoveIcon />
                                      </ActionButton>
                                    </Tooltip>
                                  </ActionButtonContainer>
                                )
                              }
                            </FlexContainer>
                          )}
                        />
                      );
                    })
                  }
                  {
                    isEditMode && (
                      <Card
                        isDark
                        isContainer
                        isSelectable
                        withSmallPadding
                        onClick={onDealFileGroupAdd}
                      >
                        <ActionButtonNewItem isLight withNoHighlight label="Add New Group" />
                      </Card>
                    )
                  }
                </Widget>
                {
                  dealTemplateCustomSections.map((dealTemplateSectionItem: DealTemplateSection): ReactElement => {
                    const formKeyDealTemplateSection: string = FormService.getIndexedFormFieldKey(
                      dealTemplateSectionItem.id,
                    );

                    return (
                      <Widget
                        isFullWidth
                        isFullHeight
                        withEditMode
                        widgetName={`${WIDGET_NAME_PREFIX_DEAL_TEMPLATE_SECTION}-${dealTemplateSectionItem.id}`}
                        isEditMode={isEditMode}
                        withDelete={isEditMode}
                        key={dealTemplateSectionItem.id}
                        defaultWidgetTitle={!isEditMode ? dealTemplateSectionItem.name : undefined}
                        onEditModeButtonClick={onEditModeButtonClick}
                        onDeleteButtonClick={(): void => {
                          onDealTemplateCustomSectionDelete(dealTemplateSectionItem.id);
                        }}
                        deleteDeps={deleteSectionDropDeps}
                        widgetTitleComponent={(
                          isEditMode ? (
                            <Fragment>
                              <FormFieldText
                                isHidden
                                name={`${customSectionsFormPrefix}[${formKeyDealTemplateSection}].id`}
                              />
                              <FormFieldText
                                isLimited
                                placeholder="Section Name"
                                name={`${customSectionsFormPrefix}[${formKeyDealTemplateSection}].name`}
                              />
                            </Fragment>
                          ) : undefined
                        )}
                      >
                        {
                          ((dealTemplateSectionItem.fields && !!dealTemplateSectionItem.fields.length)
                            || isEditMode) ? (
                            <Table>
                              <TableBody isPlain withZebraRows>
                                {
                                  dealTemplateSectionItem.fields && !!dealTemplateSectionItem.fields.length
                                  && dealTemplateSectionItem.fields.map((
                                    dealTemplateSectionFieldItem: DealTemplateSectionField,
                                  ): ReactElement => {
                                    const isDropdownField: boolean = onCheckIfDealTemplateSectionFieldDropdown(
                                      dealTemplateSectionItem.id, dealTemplateSectionFieldItem.id,
                                    );
                                    const fieldTypeText: string | null = (dealTemplateSectionFieldItem.type
                                      && fieldTypeMap && fieldTypeMap[dealTemplateSectionFieldItem.type]
                                      && fieldTypeMap[dealTemplateSectionFieldItem.type].title) || null;
                                    const fieldListTypeText: string | null = (dealTemplateSectionFieldItem.listType
                                      && fieldListTypeMap && fieldListTypeMap[dealTemplateSectionFieldItem.listType]
                                      && fieldListTypeMap[dealTemplateSectionFieldItem.listType].title) || null;
                                    const customSectionsFieldFormPrefix: string = `${customSectionsFormPrefix}`
                                      + `[${formKeyDealTemplateSection}].fields`;
                                    const formKeyDealTemplateSectionField: string = (
                                      FormService.getIndexedFormFieldKey(dealTemplateSectionFieldItem.id)
                                    );

                                    return (
                                      <TableRow key={dealTemplateSectionFieldItem.id}>
                                        <TableCell width="40%">
                                          {
                                            isEditMode ? (
                                              <Fragment>
                                                <FormFieldText
                                                  isHidden
                                                  name={`${customSectionsFieldFormPrefix}`
                                                    + `[${formKeyDealTemplateSectionField}].id`}
                                                />
                                                <FormFieldText
                                                  placeholder="Name"
                                                  name={`${customSectionsFieldFormPrefix}`
                                                    + `[${formKeyDealTemplateSectionField}].name`}
                                                />
                                              </Fragment>
                                            ) : dealTemplateSectionFieldItem.name
                                          }
                                          {
                                            !isEditMode && onCheckIfDealTemplateSectionFieldUsed(
                                              dealTemplateSectionItem.name, dealTemplateSectionFieldItem.name,
                                            ) && (
                                              <HintLinked title="This deal template field is linked to report" />
                                            )
                                          }
                                        </TableCell>
                                        <TableCell width="40%">
                                          {
                                            !isEditMode && (
                                              fieldListTypeText ? `${fieldTypeText} (${fieldListTypeText})`
                                                : fieldTypeText
                                            )
                                          }
                                          {
                                            isEditMode && (
                                              <FormFieldSelect
                                                isDisabled={fieldTypeList && !fieldTypeList.length}
                                                name={`${customSectionsFieldFormPrefix}`
                                                  + `[${formKeyDealTemplateSectionField}].type`}
                                                onChange={(event: React.ChangeEvent, value: string): void => {
                                                  onDealTemplateCustomSectionFieldTypeChange(
                                                    dealTemplateSectionItem.id, dealTemplateSectionFieldItem.id,
                                                    value,
                                                  );
                                                }}
                                              >
                                                {menuItemFieldTypeList}
                                              </FormFieldSelect>
                                            )
                                          }
                                          {
                                            isEditMode && isDropdownField && (
                                              <FormFieldSelect
                                                withBlankElement
                                                isDisabled={fieldListTypeList && !fieldListTypeList.length}
                                                name={`${customSectionsFieldFormPrefix}`
                                                  + `[${formKeyDealTemplateSectionField}].listType`}
                                              >
                                                {menuItemFieldListTypeList}
                                              </FormFieldSelect>
                                            )
                                          }
                                        </TableCell>
                                        <TableCell isTextRight={isEditMode} width="20%">
                                          {
                                            isEditMode ? (
                                              <ActionButton
                                                isInline
                                                withNoHighlight
                                                withSmallPadding
                                                onClick={(): void => {
                                                  onDealTemplateCustomSectionFieldDelete(
                                                    dealTemplateSectionItem.id, dealTemplateSectionFieldItem.id,
                                                  );
                                                }}
                                              >
                                                <RemoveIcon />
                                              </ActionButton>
                                            ) : (
                                              <VisualEditorSectionBlock isDisabled isInlineField isVisible />
                                            )
                                          }
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })
                                }
                                {
                                  isEditMode && (
                                    <TableRowAddNewItem
                                      label="Add New Line"
                                      colSpan={COLUMNS_TOTAL_CUSTOM_SECTION}
                                      onClick={(): void => {
                                        onDealTemplateCustomSectionFieldAdd(dealTemplateSectionItem.id);
                                      }}
                                    />
                                  )
                                }
                              </TableBody>
                            </Table>
                          ) : (
                            <VisualEditorSectionBlock isDisabled isVisible />
                          )
                        }
                      </Widget>
                    );
                  })
                }
                {
                  isEditMode && (
                    <Card
                      isContainer
                      isSelectable
                      withSmallPadding
                      onClick={(): void => {
                        onDealTemplateCustomSectionAdd();
                      }}
                    >
                      <ActionButtonNewItem withNoHighlight label="Add New Section" />
                    </Card>
                  )
                }
              </WidgetRow>
              <WidgetRow isColumn={!isSmallScreen}>
                <Widget isFullWidth isFullHeight defaultWidgetTitle="Investment Info">
                  <Table>
                    <TableBody isPlain withZebraRows withTwoColumns withHeaderColumn>
                      <TableRow>
                        <TableCell>
                          Deal Type
                        </TableCell>
                        <TableCell>
                          <VisualEditorSectionBlock isInlineField isDisabled isVisible isEditMode={isEditMode} />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          Deal Term
                        </TableCell>
                        <TableCell>
                          <VisualEditorSectionBlock
                            isInlineField
                            isEditMode={isEditMode}
                            isVisible={onCheckIfDealTemplateHiddenFieldVisible(DEAL_TEMPLATE_KEY_DEAL_TERM)}
                            fieldName={`${hiddenFieldsFormPrefix}[${DEAL_TEMPLATE_KEY_DEAL_TERM}].isVisible`}
                            onVisibilityChange={(isVisible: boolean): void => {
                              onDealTemplateFieldVisibilityChange(isVisible, DEAL_TEMPLATE_KEY_DEAL_TERM);
                            }}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          Total Capitalization
                        </TableCell>
                        <TableCell>
                          <VisualEditorSectionBlock
                            isInlineField
                            isEditMode={isEditMode}
                            isVisible={onCheckIfDealTemplateHiddenFieldVisible(DEAL_TEMPLATE_KEY_AMOUNT)}
                            fieldName={`${hiddenFieldsFormPrefix}[${DEAL_TEMPLATE_KEY_AMOUNT}].isVisible`}
                            onVisibilityChange={(isVisible: boolean): void => {
                              onDealTemplateFieldVisibilityChange(isVisible, DEAL_TEMPLATE_KEY_AMOUNT);
                            }}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          Projected NOI
                        </TableCell>
                        <TableCell>
                          <VisualEditorSectionBlock
                            isInlineField
                            isEditMode={isEditMode}
                            isVisible={onCheckIfDealTemplateHiddenFieldVisible(DEAL_TEMPLATE_KEY_PROJECTED_NOI_AMOUNT)}
                            fieldName={`${hiddenFieldsFormPrefix}[${DEAL_TEMPLATE_KEY_PROJECTED_NOI_AMOUNT}].isVisible`}
                            onVisibilityChange={(isVisible: boolean): void => {
                              onDealTemplateFieldVisibilityChange(isVisible, DEAL_TEMPLATE_KEY_PROJECTED_NOI_AMOUNT);
                            }}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          Appraised Value
                        </TableCell>
                        <TableCell>
                          <VisualEditorSectionBlock
                            isInlineField
                            isEditMode={isEditMode}
                            isVisible={onCheckIfDealTemplateHiddenFieldVisible(
                              DEAL_TEMPLATE_KEY_APPRAISED_VALUE_AMOUNT,
                            )}
                            fieldName={`${hiddenFieldsFormPrefix}`
                              + `[${DEAL_TEMPLATE_KEY_APPRAISED_VALUE_AMOUNT}].isVisible`}
                            onVisibilityChange={(isVisible: boolean): void => {
                              onDealTemplateFieldVisibilityChange(isVisible, DEAL_TEMPLATE_KEY_APPRAISED_VALUE_AMOUNT);
                            }}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          Loan Amount
                        </TableCell>
                        <TableCell>
                          <VisualEditorSectionBlock
                            isInlineField
                            isEditMode={isEditMode}
                            isVisible={onCheckIfDealTemplateHiddenFieldVisible(DEAL_TEMPLATE_KEY_LOAN_AMOUNT)}
                            fieldName={`${hiddenFieldsFormPrefix}[${DEAL_TEMPLATE_KEY_LOAN_AMOUNT}].isVisible`}
                            onVisibilityChange={(isVisible: boolean): void => {
                              onDealTemplateFieldVisibilityChange(isVisible, DEAL_TEMPLATE_KEY_LOAN_AMOUNT);
                            }}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          RFP
                        </TableCell>
                        <TableCell>
                          <VisualEditorSectionBlock
                            isInlineField
                            isEditMode={isEditMode}
                            isVisible={onCheckIfDealTemplateHiddenFieldVisible(DEAL_TEMPLATE_KEY_RFP)}
                            fieldName={`${hiddenFieldsFormPrefix}[${DEAL_TEMPLATE_KEY_RFP}].isVisible`}
                            onVisibilityChange={(isVisible: boolean): void => {
                              onDealTemplateFieldVisibilityChange(isVisible, DEAL_TEMPLATE_KEY_RFP);
                            }}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          Estimated TDC
                        </TableCell>
                        <TableCell>
                          <VisualEditorSectionBlock
                            isInlineField
                            isEditMode={isEditMode}
                            isVisible={onCheckIfDealTemplateHiddenFieldVisible(DEAL_TEMPLATE_KEY_ESTIMATED_TDC)}
                            fieldName={`${hiddenFieldsFormPrefix}[${DEAL_TEMPLATE_KEY_ESTIMATED_TDC}].isVisible`}
                            onVisibilityChange={(isVisible: boolean): void => {
                              onDealTemplateFieldVisibilityChange(isVisible, DEAL_TEMPLATE_KEY_ESTIMATED_TDC);
                            }}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          Equity Required
                        </TableCell>
                        <TableCell>
                          <VisualEditorSectionBlock
                            isInlineField
                            isEditMode={isEditMode}
                            isVisible={onCheckIfDealTemplateHiddenFieldVisible(DEAL_TEMPLATE_KEY_EQUITY_REQUIRED)}
                            fieldName={`${hiddenFieldsFormPrefix}[${DEAL_TEMPLATE_KEY_EQUITY_REQUIRED}].isVisible`}
                            onVisibilityChange={(isVisible: boolean): void => {
                              onDealTemplateFieldVisibilityChange(isVisible, DEAL_TEMPLATE_KEY_EQUITY_REQUIRED);
                            }}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          Asking Price
                        </TableCell>
                        <TableCell>
                          <VisualEditorSectionBlock
                            isInlineField
                            isEditMode={isEditMode}
                            isVisible={onCheckIfDealTemplateHiddenFieldVisible(DEAL_TEMPLATE_KEY_ASKING_PRICE)}
                            fieldName={`${hiddenFieldsFormPrefix}[${DEAL_TEMPLATE_KEY_ASKING_PRICE}].isVisible`}
                            onVisibilityChange={(isVisible: boolean): void => {
                              onDealTemplateFieldVisibilityChange(isVisible, DEAL_TEMPLATE_KEY_ASKING_PRICE);
                            }}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          Loan Type
                        </TableCell>
                        <TableCell>
                          <VisualEditorSectionBlock
                            isInlineField
                            isEditMode={isEditMode}
                            isVisible={onCheckIfDealTemplateHiddenFieldVisible(DEAL_TEMPLATE_KEY_LOAN_TYPE)}
                            fieldName={`${hiddenFieldsFormPrefix}[${DEAL_TEMPLATE_KEY_LOAN_TYPE}].isVisible`}
                            onVisibilityChange={(isVisible: boolean): void => {
                              onDealTemplateFieldVisibilityChange(isVisible, DEAL_TEMPLATE_KEY_LOAN_TYPE);
                            }}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          Extension Conditions
                        </TableCell>
                        <TableCell>
                          <VisualEditorSectionBlock
                            isInlineField
                            isEditMode={isEditMode}
                            isVisible={onCheckIfDealTemplateHiddenFieldVisible(DEAL_TEMPLATE_KEY_EXTENSION_CONDITIONS)}
                            fieldName={`${hiddenFieldsFormPrefix}[${DEAL_TEMPLATE_KEY_EXTENSION_CONDITIONS}].isVisible`}
                            onVisibilityChange={(isVisible: boolean): void => {
                              onDealTemplateFieldVisibilityChange(isVisible, DEAL_TEMPLATE_KEY_EXTENSION_CONDITIONS);
                            }}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          Equity Amount
                        </TableCell>
                        <TableCell>
                          <VisualEditorSectionBlock
                            isInlineField
                            isEditMode={isEditMode}
                            isVisible={onCheckIfDealTemplateHiddenFieldVisible(DEAL_TEMPLATE_KEY_EQUITY_AMOUNT)}
                            fieldName={`${hiddenFieldsFormPrefix}[${DEAL_TEMPLATE_KEY_EQUITY_AMOUNT}].isVisible`}
                            onVisibilityChange={(isVisible: boolean): void => {
                              onDealTemplateFieldVisibilityChange(isVisible, DEAL_TEMPLATE_KEY_EQUITY_AMOUNT);
                            }}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          Total Funded
                        </TableCell>
                        <TableCell>
                          <VisualEditorSectionBlock
                            isInlineField
                            isEditMode={isEditMode}
                            isVisible={onCheckIfDealTemplateHiddenFieldVisible(DEAL_TEMPLATE_KEY_TOTAL_FUNDED)}
                            fieldName={`${hiddenFieldsFormPrefix}[${DEAL_TEMPLATE_KEY_TOTAL_FUNDED}].isVisible`}
                            onVisibilityChange={(isVisible: boolean): void => {
                              onDealTemplateFieldVisibilityChange(isVisible, DEAL_TEMPLATE_KEY_TOTAL_FUNDED);
                            }}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          Total Unfunded
                        </TableCell>
                        <TableCell>
                          <VisualEditorSectionBlock
                            isInlineField
                            isEditMode={isEditMode}
                            isVisible={onCheckIfDealTemplateHiddenFieldVisible(DEAL_TEMPLATE_KEY_TOTAL_UNFUNDED)}
                            fieldName={`${hiddenFieldsFormPrefix}[${DEAL_TEMPLATE_KEY_TOTAL_UNFUNDED}].isVisible`}
                            onVisibilityChange={(isVisible: boolean): void => {
                              onDealTemplateFieldVisibilityChange(isVisible, DEAL_TEMPLATE_KEY_TOTAL_UNFUNDED);
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  <ExpansionGroup isFixed isExpanded isDefaultExpanded headerComponent="Capitalization">
                    <VisualEditorSectionBlock
                      isEditMode={isEditMode}
                      isVisible={onCheckIfDealTemplateHiddenFieldVisible(DEAL_TEMPLATE_KEY_DEAL_CAPITALIZATION)}
                      fieldName={`${hiddenFieldsFormPrefix}[${DEAL_TEMPLATE_KEY_DEAL_CAPITALIZATION}].isVisible`}
                      onVisibilityChange={(isVisible: boolean): void => {
                        onDealTemplateFieldVisibilityChange(isVisible, DEAL_TEMPLATE_KEY_DEAL_CAPITALIZATION);
                      }}
                    />
                    <Table isFullWidth>
                      <TableHead isSubHeader withFixedCellsSize>
                        {
                          dealTemplateInitialDataDealCapitalizationSourcesGroups.map((
                            dealCapitalizationGroup: DealCapitalizationGroup,
                          ): ReactElement => {
                            const formKeyDealCapitalizationGroup: string = FormService.getIndexedFormFieldKey(
                              dealCapitalizationGroup.id,
                            );

                            return (
                              <TableRow key={dealCapitalizationGroup.id}>
                                <TableCell>
                                  {
                                    (!isEditMode || dealCapitalizationGroup.isSystem) && dealCapitalizationGroup.name
                                  }
                                  {
                                    (isEditMode && !dealCapitalizationGroup.isSystem) && (
                                      <Fragment>
                                        <FormFieldText
                                          isHidden
                                          name={`${initialDataFormPrefix}.dealCapitalization.`
                                            + `sources[${formKeyDealCapitalizationGroup}].id`}
                                        />
                                        <FormFieldText
                                          isLight
                                          isLimited
                                          withUnderline
                                          placeholder="Name"
                                          name={`${initialDataFormPrefix}.dealCapitalization.`
                                            + `sources[${formKeyDealCapitalizationGroup}].name`}
                                        />
                                      </Fragment>
                                    )
                                  }
                                </TableCell>
                                <TableCell isTextRight>
                                  {
                                    isEditMode && !dealCapitalizationGroup.isSystem && (
                                      <ActionButton
                                        isLight
                                        isInline
                                        withNoHighlight
                                        withNoPadding
                                        onClick={(): void => {
                                          onDealCapitalizationSourceGroupDelete(dealCapitalizationGroup.id);
                                        }}
                                      >
                                        <RemoveIcon />
                                      </ActionButton>
                                    )
                                  }
                                </TableCell>
                              </TableRow>
                            );
                          })
                        }
                        {
                          isEditMode && (
                            <TableRowAddNewItem
                              isLight
                              label="Add New Group"
                              colSpan={COLUMNS_TOTAL_COMMON}
                              onClick={onDealCapitalizationSourceGroupAdd}
                            />
                          )
                        }
                      </TableHead>
                    </Table>
                  </ExpansionGroup>
                  <ExpansionGroup isFixed isExpanded headerComponent="Capitalization Metrics">
                    <VisualEditorSectionBlock
                      withNoEdit
                      isEditMode={isEditMode}
                      isVisible={onCheckIfDealTemplateHiddenFieldVisible(DEAL_TEMPLATE_KEY_DEAL_CAPITALIZATION)}
                    />
                  </ExpansionGroup>
                  <ExpansionGroup isFixed isExpanded headerComponent="Investment Returns">
                    <VisualEditorSectionBlock
                      isEditMode={isEditMode}
                      isVisible={onCheckIfDealTemplateHiddenFieldVisible(DEAL_TEMPLATE_KEY_DEAL_INVESTMENT_RETURNS)}
                      fieldName={`${hiddenFieldsFormPrefix}[${DEAL_TEMPLATE_KEY_DEAL_INVESTMENT_RETURNS}].isVisible`}
                      onVisibilityChange={(isVisible: boolean): void => {
                        onDealTemplateFieldVisibilityChange(isVisible, DEAL_TEMPLATE_KEY_DEAL_INVESTMENT_RETURNS);
                      }}
                    />
                    {
                      dealTemplateInitialDataDealInvestmentReturnsItem
                      && !!dealTemplateInitialDataDealInvestmentReturnsItem.length && (
                        <Table isFullWidth>
                          <TableBody isSubHeader withFixedCellsSize>
                            {
                              dealTemplateInitialDataDealInvestmentReturnsItem.map((
                                dealInvestmentReturnsItem: DealInvestmentReturnsItem,
                              ): ReactElement => {
                                const formKeyDealInvestmentReturnsItem: string = FormService.getIndexedFormFieldKey(
                                  dealInvestmentReturnsItem.id,
                                );

                                return (
                                  <TableRow key={dealInvestmentReturnsItem.id}>
                                    <TableCell>
                                      {
                                        !isEditMode && dealInvestmentReturnsItem.name
                                      }
                                      {
                                        isEditMode && (
                                          <Fragment>
                                            <FormFieldText
                                              isHidden
                                              name={`${initialDataFormPrefix}.dealInvestmentReturns`
                                                + `[${formKeyDealInvestmentReturnsItem}].id`}
                                            />
                                            <FormFieldText
                                              isLimited
                                              withUnderline
                                              placeholder="Name"
                                              name={`${initialDataFormPrefix}.dealInvestmentReturns`
                                                + `[${formKeyDealInvestmentReturnsItem}].name`}
                                            />
                                          </Fragment>
                                        )
                                      }
                                    </TableCell>
                                    <TableCell isTextRight>
                                      {
                                        isEditMode && (
                                          <ActionButton
                                            isInline
                                            withNoHighlight
                                            withNoPadding
                                            onClick={(): void => {
                                              onDealInvestmentReturnsItemDelete(dealInvestmentReturnsItem.id);
                                            }}
                                          >
                                            <RemoveIcon />
                                          </ActionButton>
                                        )
                                      }
                                    </TableCell>
                                  </TableRow>
                                );
                              })
                            }
                            {
                              isEditMode && (
                                <TableRowAddNewItem
                                  colSpan={COLUMNS_TOTAL_COMMON}
                                  onClick={onDealInvestmentReturnsItemAdd}
                                />
                              )
                            }
                          </TableBody>
                        </Table>
                      )
                    }
                  </ExpansionGroup>
                </Widget>
                <Widget isFullWidth isFullHeight defaultWidgetTitle="Milestones">
                  <VisualEditorSectionBlock
                    isEditMode={isEditMode}
                    isVisible={onCheckIfDealTemplateHiddenFieldVisible(DEAL_TEMPLATE_KEY_DEAL_MILESTONES)}
                    fieldName={`${hiddenFieldsFormPrefix}[${DEAL_TEMPLATE_KEY_DEAL_MILESTONES}].isVisible`}
                    onVisibilityChange={(isVisible: boolean): void => {
                      onDealTemplateFieldVisibilityChange(isVisible, DEAL_TEMPLATE_KEY_DEAL_MILESTONES);
                    }}
                  />
                  <Table>
                    <TableBody isPlain withZebraRows withTwoColumns withHeaderColumn>
                      <TableRow>
                        <TableCell>
                          Received Date
                        </TableCell>
                        <TableCell>
                          <VisualEditorSectionBlock isDisabled isVisible isInlineField isEditMode={isEditMode} />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          Closed Date
                        </TableCell>
                        <TableCell>
                          <VisualEditorSectionBlock isDisabled isVisible isInlineField isEditMode={isEditMode} />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          Bid Date
                        </TableCell>
                        <TableCell>
                          <VisualEditorSectionBlock isDisabled isVisible isInlineField isEditMode={isEditMode} />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Widget>
                <Widget isFullWidth isFullHeight defaultWidgetTitle="Deal Contacts">
                  <VisualEditorSectionBlock
                    isEditMode={isEditMode}
                    isVisible={onCheckIfDealTemplateHiddenFieldVisible(DEAL_TEMPLATE_KEY_DEAL_CONTACTS)}
                    fieldName={`${hiddenFieldsFormPrefix}[${DEAL_TEMPLATE_KEY_DEAL_CONTACTS}].isVisible`}
                    onVisibilityChange={(isVisible: boolean): void => {
                      onDealTemplateFieldVisibilityChange(isVisible, DEAL_TEMPLATE_KEY_DEAL_CONTACTS);
                    }}
                  />
                  {
                    dealTemplateInitialDataDealContacts.map((dealContactsGroup: DealContactGroup): ReactElement => {
                      const formKeyDealContactsGroupItem: string = FormService.getIndexedFormFieldKey(
                        dealContactsGroup.id,
                      );

                      return (
                        <ExpansionGroup
                          isFixed
                          isExpanded
                          isDefaultExpanded
                          key={dealContactsGroup.id}
                          headerComponent={(
                            <FlexContainer isSpread>
                              <FlexContainer isSpread isStart>
                                {
                                  !isEditMode && dealContactsGroup.name
                                }
                                {
                                  isEditMode && (
                                    <Fragment>
                                      <FormFieldText
                                        isHidden
                                        name={`${initialDataFormPrefix}.dealContacts`
                                          + `[${formKeyDealContactsGroupItem}].id`}
                                      />
                                      <FormFieldText
                                        isLight
                                        isLimited
                                        withUnderline
                                        placeholder="Name"
                                        name={`${initialDataFormPrefix}.dealContacts`
                                          + `[${formKeyDealContactsGroupItem}].name`}
                                      />
                                    </Fragment>
                                  )
                                }
                              </FlexContainer>
                              {
                                isEditMode && (
                                  <ActionButtonContainer>
                                    <Tooltip title="Delete">
                                      <ActionButton
                                        isLight
                                        withNoPadding
                                        onClick={(): void => {
                                          onDealContactsGroupDelete(dealContactsGroup.id);
                                        }}
                                      >
                                        <RemoveIcon />
                                      </ActionButton>
                                    </Tooltip>
                                  </ActionButtonContainer>
                                )
                              }
                            </FlexContainer>
                          )}
                        />
                      );
                    })
                  }
                  {
                    isEditMode && (
                      <Card
                        isDark
                        isContainer
                        isSelectable
                        withSmallPadding
                        onClick={onDealContactsGroupAdd}
                      >
                        <ActionButtonNewItem isLight withNoHighlight label="Add New Group" />
                      </Card>
                    )
                  }
                </Widget>
              </WidgetRow>
            </FlexContainer>
          </PaddedFlexContainer>
        )
      }
    </SinglePageTab>
  );
});

export default DealTemplateSummary;
